import * as React from "react";

import {cc}                              from "@Common/react/callback-chain";
import {cs}                              from "@Common/react/chain-services";
import {consumeContext, provideContext1} from "@Common/react/context";
import {fragments}                       from "@Common/react/fragments";
import {Invoke}                          from "@Common/react/invoke";
import {keyCombo}                        from "@Common/react/key-combo";
import {Load}                            from "@Common/react/load";
import {Load2}                           from "@Common/react/load2";
import {OnMounted}                       from "@Common/react/on-mounted";
import {ConditionalValue}                from "@Common/react/shadow/conditional-value";
import {Static}                          from "@Common/react/static";
import {UseState}                        from "@Common/react/use-state";
import {GlobalKeyDown2}                  from "@Common/ui-components/browser/global-key-down-2";
import {BubbleNoti2}                     from "@Common/ui-components/bubble-noti-2/bubble-noti-2";
import {cache0}                          from "@Common/utils/cache0";
import {cache1}                          from "@Common/utils/cache1";
import {cascadeFind}                     from "@Common/utils/cascade";
import {findMaxE, replaceIndex}          from "@Common/utils/collections";
import {chain}                           from "@Common/utils/fs";
import {omit}                            from "@Common/utils/objects";

import {AlertErrorDialog2} from "./alert-error-dialog-2";
import {NoEnrollment}      from "./no-enrollment/no-enrollment";
import {WithEnrollment}    from "./with-enrollment/with-enrollment";

export const ScheduledCampaignsPanel = ({agentId=undefined,branchId, next: rootNext}) => cs(
    consumeContext("apis"),
    consumeContext("userLevels"),

    ["program", ({apis, userLevels}, next) => Load2({
        fetch: () => apis.listings.enrollment.getProgram({branchId, userLevel: userLevels.userLevel}),
        next,
    })],
    ["renderParams", ({program}, next) => rootNext({
        ...program.loading ? {
            loading: program.loading,
        } : {
            available: !!program.value,
            linkLabel: program.value?.programPreferences.linkName,
        },
        render: ({initStep, next: next1}) => next({initStep, next: next1}),
    })],

    consumeContext("session"),
    ({session}, next) => fragments(
        next(),
        agentId && !session.loading && session.value?.agentId !== agentId && Invoke({
            action: async () => {
                await session.applyEmulation({agentId, branchId});
            },
        })
    ),

    ["bubbleNoti", (_, next) => BubbleNoti2({next,})],
    // ({bubbleNoti}, next) => fragments(
    //     next(),
    //     isDevQuan() && GlobalKeyDown2({
    //         check: keyCombo("z"),
    //         action: () => bubbleNoti.addMessage({
    //             content: <>
    //                 <b>Enrollment Complete!</b> You'll be sent an email with confirmation of your selections. Use the Dashboard on this page to manage your enrollment in the future.
    //             </>
    //         }),
    //     })
    // ),
    ({program}, next) => (
        program.loading ? (
            "Loading enrollment program info..."
        ) : !program.value ? (
            "No enrollment program available"
        ) : (
            next()
        )
    ),

    ["enrollments", ({apis, program}, next) => Load2({
        _key: program.value?.idProgram,
        keepOutdatedValue: true,
        fetch: () => !program.value?.idProgram ? null : apis.listings.enrollment.getUserEnrollments({agentId, programId: program.value.idProgram}),
        next,
    })],

    // ["enrollment", (_, next) => UseState({
    //     ...isDevQuan() && {
    //         initValue: {},
    //     },
    //     next,
    // })],

    ["getStyleTemplate", ({program, apis}, next) => cs(
        ["styleTemplates", (_, next) => {
            const subIds = (
                program.value?.programPreferences?.subCategoryIDs ??
                [program.value?.programPreferences?.subCategoryID]
            ).filter(v=>v);
            return Load({
                _key: JSON.stringify(subIds),
                fetch: () => !subIds?.length ? null : apis.next.getStyleTemplate(subIds),
                next,
            });
        }],
        ({styleTemplates}) => next((templateId) => cascadeFind(styleTemplates, "[*].templates[*]", (template) => template.id == templateId && template)),
    )],


    ({enrollments}, next) => (
        !enrollments.value ? (
            "Loading user enrollment info..."
        ) : (
            next()
        )
    ),

    ["setEnrollment", ({enrollments, program}, next) => next((newValue) => {
        if (!newValue) {
            enrollments.onChange([
                {
                    updated_at: new Date().toString(),
                    user_prog_prefs: {
                        idProgram: program.value.idProgram,
                    },
                },
                ...enrollments.value,
            ]);
        } else {
            const i = enrollments.value.findIndex((r) => r._id !== newValue._id ? r : newValue);
            const newRecord = {
                updated_at: new Date().toString(),
                user_prog_prefs: {
                    idProgram: program.value.idProgram,
                    ...newValue,
                },
            };
            if (i === -1) {
                enrollments.onChange([
                    newRecord,
                    ...enrollments.value,
                ]);
            } else {
                enrollments.onChange(
                    replaceIndex(i, enrollments.value, newRecord)
                );
            }
        }
    })],

    ["enrollment", ({enrollments, program}, next) => next(cc(enrollments.value ?? [],[
        (_, next) => next(_.filter((e) => e.user_prog_prefs?.idProgram === program.value.idProgram)),
        (_, next) => (
            _.find((e) => e.user_prog_prefs?.enrolled)?.user_prog_prefs ?? next(_)
        ),
        (_) => findMaxE<any, any>(_, (r) => new Date(r.updated_at).getTime())?.user_prog_prefs,
    ]))],

    // ({enrollment}, next) => fragments(
    //     next(),
    //     isDevQuan() && GlobalKeyDown2({
    //         check: keyCombo("ctrl+K"),
    //         action: () => scnv(enrollment, ["enrolled"], v=>!v),
    //     }),
    // ),

    ["initStep", ({renderParams: {initStep}}, next) => cs(
        ["initialRun", (_, next) => UseState({initValue: true, next})],
        ({initialRun}, next) => fragments(
            next(),
            OnMounted({
                action: () => initialRun.onChange(false),
            }),
        ),
        ({initialRun}) => next(initialRun.value ? initStep : null),
    )],
    ["saveEnrollment", ({program, setEnrollment, enrollments, apis, bubbleNoti}, next) => cs(

        consumeContext("session"),
        consumeContext("company"),
        ["prepareEnrollmentData", ({session,company}, next) => next((enrollment) => ({
            ...enrollment,
            idAgent: agentId ?? session.value.agentId,
            idCompany: company.idCompany,
            idBranch: branchId ?? session.value.branchId,
            idProgram: program.value.idProgram,
        }))],

        ["alertErrorDialog", (_, next) => AlertErrorDialog2({next})],
        ({prepareEnrollmentData, alertErrorDialog}) => next(async (newValue, {inSetupModal=false, newEnrollment=false, ...options}={}) => {
            if (!newValue) {
                setEnrollment(null);
            } else {

                try {
                    const result = await chain(
                        newValue,
                        (_) => !_?.unenroll_reason ? _ : omit(_, ["_id", "unenroll_reason"]),
                        (_) => prepareEnrollmentData(_),
                        (_) => apis.listings.enrollment.saveUserEnrollment(_, {newEnrollment, ...options}),
                    );
                    setEnrollment(result);

                    if (newEnrollment) {
                        bubbleNoti.addMessage({
                            content: <><b>Enrollment Complete!</b> You'll be sent an email with confirmation of your selections. Use the Dashboard on this page to manage your enrollment in the future.</>
                        });
                    }
                } catch (e) {
                    if (e.error) {
                        if (e.error.message === "Already enrolled!") {

                            await enrollments.reload();

                            await alertErrorDialog.show({
                                messages: [
                                    "It appears you already have an active enrollment.  We have automatically loaded the existing enrollment information into this session.",
                                    "Any recent changes made might not have saved, so please review your selections and make any updates as needed.",
                                ],
                            });

                            return {error: "out_of_sync"};
                        }
                        // e.error.message === "Already enrolled!"
                        alert(e.error.message ?? "Unknown error");
                    } else {
                        alert("ERROR: " + e.toString());
                    }
                }
            }

        }),
    )],
    ({program}, next) => fragments(
        next(),
        GlobalKeyDown2({
            check: keyCombo("ctrl+shift+p"),
            action: () => {
                console.log("program", program.value);
            },
        }),
    ),

    ({}, next) => loadProofImgsCache({next}),

    ["content", ({enrollment, program, initStep, getStyleTemplate, saveEnrollment}, next) => ConditionalValue({
        _key: !enrollment?.enrolled,
        renderShadow: (next) => (
            !enrollment?.enrolled ? (
                NoEnrollment({
                    enrollment,
                    initStep,
                    // saveUserEnrollment,
                    program: program.value,
                    saveEnrollment,
                    agentId,
                    branchId,
                    getStyleTemplate,
                    next,
                })
            ) : (
                WithEnrollment({
                    enrollment,
                    initStep,
                    // saveUserEnrollment,
                    program: program.value,
                    agentId,
                    branchId,
                    saveEnrollment,
                    getStyleTemplate,
                    // getHistoricRuns,
                    next,
                })
            )
        ),
        next,
    })],
    ({content, renderParams}) => renderParams.next({
        navBlock: content.navBlock,
        render: () => content.render(),
    }),
);

const loadProofImgsCache = ({next}) => cs(
    consumeContext("ccApis"),
    ["proofImgsCache", ({ccApis}, next) => Static({
        getInitValue: () => cache1((templateId) => {
            let loaded = null;
            return ({
                getLoaded: () => loaded,
                load: cache0(async () => {
                    const ret = await ccApis.getHighResProducts1({templateId});
                    loaded = ret;
                    return ret;
                }),
            });
        }),
        next,
    })],
    ({proofImgsCache}) => provideContext1("proofImgsCache", proofImgsCache, next),
);

